import { KeyRound, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, errorMessage } from "@/lib/api";
import { Button, Callout, Field, Input } from "@/components/ui/core";

interface Props {
  setupRequired: boolean;
  onSignedIn: (user: { username: string; csrf: string }) => void;
  onSetupDone: () => void;
}

function Shell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-bg px-4 py-10">
      <div className="w-full max-w-[380px]">
        <div className="mb-6 flex flex-col items-center text-center">
          <svg viewBox="0 0 64 64" className="mb-4 size-12" aria-hidden>
            <rect width="64" height="64" rx="14" fill="var(--panel-3)" />
            <path d="M12 44 L24 32 L33 38 L52 18" fill="none" stroke="var(--up)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="52" cy="18" r="5" fill="var(--accent)" />
          </svg>
          <h1 className="text-[20px] font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 text-[13px] text-muted">{subtitle}</p>
        </div>
        <div className="rounded-2xl border border-line bg-panel p-5 shadow-panel">{children}</div>
        <p className="mt-6 text-center text-[11.5px] text-faint">Charts by TradingView Lightweight Charts</p>
      </div>
    </div>
  );
}

export function LoginPage({ setupRequired, onSignedIn, onSetupDone }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [code, setCode] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submitLogin(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post<{ totp_required?: boolean; user?: { username: string; csrf: string } }>("/api/auth/login", {
        username,
        password,
        totp: needTotp ? totp : undefined,
      });
      if (r.totp_required) setNeedTotp(true);
      else if (r.user) onSignedIn(r.user);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitSetup(e: FormEvent) {
    e.preventDefault();
    if (password !== password2) return setError("The passwords differ.");
    setBusy(true);
    setError("");
    try {
      await api.post("/api/auth/setup", { code, username, password });
      onSetupDone();
      const r = await api.post<{ user?: { username: string; csrf: string } }>("/api/auth/login", { username, password });
      if (r.user) onSignedIn(r.user);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (setupRequired)
    return (
      <Shell title="Set up AlgoTrader" subtitle="Create the account that controls this server.">
        <form onSubmit={submitSetup} className="grid gap-3.5">
          <Callout tone="accent" icon={<KeyRound className="size-4" />}>
            Enter the setup code printed in the server log when it started. It is also saved in <span className="mono">secrets/setup_code</span> in the data folder.
          </Callout>
          <Field label="Setup code">
            <Input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" className="mono" required />
          </Field>
          <Field label="Username">
            <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required minLength={3} />
          </Field>
          <Field label="Password" hint="At least 10 characters. A passphrase is best.">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required minLength={10} />
          </Field>
          <Field label="Repeat password">
            <Input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} autoComplete="new-password" required minLength={10} />
          </Field>
          {error && <div className="text-[12.5px] text-down">{error}</div>}
          <Button type="submit" variant="primary" loading={busy}>
            Create account
          </Button>
        </form>
      </Shell>
    );

  return (
    <Shell title="Sign in" subtitle="Automated trading for Indian markets">
      <form onSubmit={submitLogin} className="grid gap-3.5">
        {!needTotp ? (
          <>
            <Field label="Username">
              <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoFocus required />
            </Field>
            <Field label="Password">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            </Field>
          </>
        ) : (
          <Field label="Authenticator code" hint="The 6-digit code from your authenticator app.">
            <Input value={totp} onChange={(e) => setTotp(e.target.value)} inputMode="numeric" autoComplete="one-time-code" autoFocus className="mono text-center text-[18px] tracking-[0.4em]" maxLength={6} required />
          </Field>
        )}
        {error && <div className="text-[12.5px] text-down">{error}</div>}
        <Button type="submit" variant="primary" loading={busy} icon={needTotp ? <ShieldCheck className="size-4" /> : undefined}>
          {needTotp ? "Verify" : "Sign in"}
        </Button>
      </form>
    </Shell>
  );
}
